import React from 'react';
import PastillePresence from '../common/PastillePresence';
import { Presence, EtatSupervision } from '../../hooks/usePresence';
import { nomAffichageBoitier } from '../../utils/nomBoitier';
import { 
  Card, 
  CardContent, 
  CardActionArea,
  Typography,
  Chip,
  Box,
  Stack,
} from '@mui/material';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WaterIcon from '@mui/icons-material/Water';
import BoltIcon from '@mui/icons-material/Bolt';
import SpeedIcon from '@mui/icons-material/Speed';
import OpacityIcon from '@mui/icons-material/Opacity';
import CompressIcon from '@mui/icons-material/Compress';
import ScienceIcon from '@mui/icons-material/Science';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SensorsIcon from '@mui/icons-material/Sensors';
import SensorsOffIcon from '@mui/icons-material/SensorsOff';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import PowerIcon from '@mui/icons-material/Power';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ElectricMeterIcon from '@mui/icons-material/ElectricMeter';
import WavesIcon from '@mui/icons-material/Waves';
import HeightIcon from '@mui/icons-material/Height';
import TimerIcon from '@mui/icons-material/Timer';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import AirIcon from '@mui/icons-material/Air';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ScaleIcon from '@mui/icons-material/Scale';
import StraightenIcon from '@mui/icons-material/Straighten';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BoitierStatus, DeviceField, SensorReading } from '../../services/ApiService';

// Mapping des noms d'icônes vers les composants
const iconMap: { [key: string]: React.ElementType } = {
  'Thermostat': ThermostatIcon,
  'Water': WaterIcon,
  'Bolt': BoltIcon,
  'Speed': SpeedIcon,
  'Opacity': OpacityIcon,
  'Compress': CompressIcon,
  'Science': ScienceIcon,
  'ShowChart': ShowChartIcon,
  'ElectricMeter': ElectricMeterIcon,
  'Waves': WavesIcon,
  'Height': HeightIcon,
  'Timer': TimerIcon,
  'Air': AirIcon,
  'WaterDrop': WaterDropIcon,
  'Scale': ScaleIcon,
  'Straighten': StraightenIcon,
};

/**
 * Système robuste de détection d'icônes pour grandeurs physiques
 * Gère: abréviations, fautes d'orthographe, variations de casse, etc.
 */
const physicalQuantityPatterns: Array<{ patterns: string[], icon: React.ElementType }> = [
  // TEMPÉRATURE
  {
    patterns: ['temp', 'température', 'temperature', 'tmp', 'degre', 'degree', 'celsius', 'kelvin', 'fahrenheit', 'thermo', 'chaleur', 'heat', 'chaud', 'froid', 'cold', 'hot'],
    icon: ThermostatIcon
  },
  // pH (acidité/basicité)
  {
    patterns: ['ph', 'acidite', 'acidity', 'acidité', 'basique', 'alcalin', 'alkalin'],
    icon: ScienceIcon
  },
  // CONDUCTIVITÉ ÉLECTRIQUE
  {
    patterns: ['cond', 'conductivite', 'conductivité', 'conductivity', 'condu', 'siemens', 'ms/cm', 'µs/cm', 'us/cm', 'mho'],
    icon: BoltIcon
  },
  // DÉBIT
  {
    patterns: ['debit', 'débit', 'flow', 'flux', 'ecoulement', 'écoulement', 'l/min', 'l/h', 'm3/h', 'gpm', 'litre', 'gallon'],
    icon: OpacityIcon
  },
  // PRESSION
  {
    patterns: ['press', 'pression', 'pressure', 'bar', 'pascal', 'psi', 'atm', 'mbar', 'hpa', 'kpa', 'mpa', 'torr'],
    icon: CompressIcon
  },
  // NIVEAU / HAUTEUR
  {
    patterns: ['niveau', 'level', 'niv', 'lvl', 'hauteur', 'height', 'profondeur', 'depth', 'remplissage', 'fill'],
    icon: HeightIcon
  },
  // COURANT ÉLECTRIQUE
  {
    patterns: ['courant', 'current', 'ampere', 'amp', 'ampère', 'intensite', 'intensité', 'intensity', 'ma', 'µa'],
    icon: BoltIcon
  },
  // TENSION ÉLECTRIQUE
  {
    patterns: ['tension', 'voltage', 'volt', 'potentiel', 'potential', 'ddp', 'fem', 'emf', 'mv', 'kv'],
    icon: BoltIcon
  },
  // PUISSANCE
  {
    patterns: ['puissance', 'power', 'watt', 'kw', 'mw', 'energie', 'energy', 'joule', 'kwh'],
    icon: PowerIcon
  },
  // DURETÉ
  {
    patterns: ['durete', 'dureté', 'hardness', 'hard', 'th', 'tac', 'calcium', 'calcaire', 'calci'],
    icon: ScaleIcon
  },
  // TURBIDITÉ
  {
    patterns: ['turbidite', 'turbidité', 'turbidity', 'turb', 'ntu', 'fnu', 'clarte', 'clarté', 'clarity', 'trouble'],
    icon: WavesIcon
  },
  // OXYGÈNE DISSOUS
  {
    patterns: ['oxygene', 'oxygène', 'oxygen', 'o2', 'do', 'dissous', 'dissolved'],
    icon: AirIcon
  },
  // CHLORE
  {
    patterns: ['chlore', 'chlorine', 'cl', 'chlor', 'desinfect', 'désinfect'],
    icon: WaterDropIcon
  },
  // REDOX / ORP
  {
    patterns: ['redox', 'orp', 'potentiel redox', 'oxydoreduction', 'oxydo'],
    icon: ScienceIcon
  },
  // SALINITÉ
  {
    patterns: ['salinite', 'salinité', 'salinity', 'sal', 'sel', 'salt', 'nacl', 'tds'],
    icon: WavesIcon
  },
  // CONCENTRATION
  {
    patterns: ['concentration', 'conc', 'ppm', 'ppb', 'mg/l', 'g/l', 'mol', 'molar'],
    icon: ScienceIcon
  },
  // VITESSE
  {
    patterns: ['vitesse', 'speed', 'velocity', 'rpm', 'tr/min', 'tour', 'rotation'],
    icon: SpeedIcon
  },
  // TEMPS / DURÉE
  {
    patterns: ['temps', 'time', 'duree', 'durée', 'duration', 'timer', 'chrono', 'minute', 'seconde', 'heure'],
    icon: TimerIcon
  },
  // MASSE / POIDS
  {
    patterns: ['masse', 'mass', 'poids', 'weight', 'kg', 'gramme', 'gram', 'tonne', 'livre', 'pound'],
    icon: ScaleIcon
  },
  // LONGUEUR / DISTANCE
  {
    patterns: ['longueur', 'length', 'distance', 'metre', 'meter', 'mm', 'cm', 'epaisseur', 'épaisseur', 'thickness'],
    icon: StraightenIcon
  },
  // VOLUME
  {
    patterns: ['volume', 'vol', 'capacite', 'capacité', 'capacity', 'litre', 'liter', 'm3', 'gallon'],
    icon: WaterIcon
  },
  // RÉSISTIVITÉ
  {
    patterns: ['resistivite', 'résistivité', 'resistivity', 'resist', 'ohm', 'megohm'],
    icon: BoltIcon
  },
];

/**
 * Normalise une chaîne pour la comparaison:
 * - Minuscules
 * - Supprime accents
 * - Supprime caractères spéciaux
 */
const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .replace(/[^a-z0-9]/g, ''); // Garde uniquement lettres et chiffres
};

// Icônes par défaut selon le code du champ (système robuste)
const getDefaultIcon = (fieldCode: string): React.ElementType => {
  const normalized = normalizeString(fieldCode);
  
  // Parcourir tous les patterns de grandeurs physiques
  for (const { patterns, icon } of physicalQuantityPatterns) {
    for (const pattern of patterns) {
      const normalizedPattern = normalizeString(pattern);
      // Match si le pattern est contenu dans le code ou vice-versa
      // Ignorer les patterns trop courts (< 2 chars) pour éviter les faux positifs
      if (normalizedPattern.length < 2) continue;
      if (normalized.includes(normalizedPattern) || (normalized.length >= 2 && normalizedPattern.includes(normalized))) {
        return icon;
      }
    }
  }
  
  // Fallback: icône générique de mesure
  return ShowChartIcon;
};

// Obtenir l'icône pour un champ
const getFieldIcon = (field: DeviceField): React.ElementType => {
  if (field.icon && iconMap[field.icon]) {
    return iconMap[field.icon];
  }
  return getDefaultIcon(field.field_code);
};

// Formater une valeur avec le bon nombre de décimales
const formatValue = (value: number | string | undefined | null, precision: number = 2): string => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'string') return value;
  if (typeof value !== 'number' || isNaN(value)) return 'N/A';
  return value.toFixed(precision);
};

/**
 * Gabarit fixe de la carte.
 *
 * Toutes les cartes du tableau de bord font la MEME hauteur, qu'elles
 * portent 3 mesures ou 9, avec ou sans relais, avec ou sans alerte. Chaque
 * zone a une hauteur reservee : ce qui deborde est tronque proprement
 * (ellipsis) au lieu de pousser la carte et de desaligner la grille.
 *
 * Les zones vides restent reservees. Sans cela, une carte change de hauteur
 * quand une alerte apparait ou disparait, et la grille se desaligne.
 */
const ZONE = {
  // Titre ET pastilles dans une seule zone : les pastilles suivent le titre
  // immediatement, qu'il tienne sur une ou deux lignes. Le vide eventuel
  // tombe en bas de la zone, jamais entre les deux.
  entete: 80,      // titre (2 lignes max de 1,05 rem) + 1 ligne de chips
  mesures: 160,    // 3 lignes x 3 colonnes (voir MAX_MESURES)
  horodatage: 18,  // 1 ligne : derniere mesure / hors ligne
  relais: 28,      // 1 ligne de chips, reservee meme sans regulation
  alertes: 40,     // 2 lignes max, ellipsis au-dela
} as const;

const ESPACEMENT = 8;      // gouttiere entre zones (px)
const PADDING_CARTE = 16;  // padding vertical du CardContent (px)

/** Hauteur totale, deduite des zones — ne jamais la coder en dur. */
export const HAUTEUR_CARTE_BAIN =
  Object.values(ZONE).reduce((total, h) => total + h, 0) +
  ESPACEMENT * (Object.keys(ZONE).length - 1) +
  PADDING_CARTE * 2;

const COLONNES_MESURES = 3;
const LIGNES_MESURES = 3;
/** Au-dela, la derniere case affiche « +N » au lieu d'une mesure. */
const MAX_MESURES = COLONNES_MESURES * LIGNES_MESURES;
const HAUTEUR_CELLULE =
  (ZONE.mesures - ESPACEMENT * (LIGNES_MESURES - 1)) / LIGNES_MESURES;

/** Texte sur une seule ligne, coupe a l'ellipsis. */
const UNE_LIGNE = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

/** Texte borne a N lignes, coupe a l'ellipsis. */
const LIGNES_MAX = (n: number) => ({
  display: '-webkit-box',
  WebkitLineClamp: n,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
});

interface BainCardProps {
  boitier: BoitierStatus;
  onClick: () => void;
  /** Présence temps réel ; à défaut, celle portée par le statut du boîtier */
  presence?: Presence;
  /**
   * LOT 7.4 — état de la supervision elle-même. Quand elle vaut
   * 'indisponible', la pastille affiche « Supervision ? ». Sans elle, l'écran
   * serait identique à celui d'un boîtier en bon état.
   */
  supervision?: EtatSupervision;
}

const BainCard: React.FC<BainCardProps> = ({ boitier, onClick, presence, supervision = 'ok' }) => {
  const { boitier_id, name, location, mqtt_topic, last_reading, fields, mqtt_connected, sensors_active, alerts, regulation_enabled, relays } = boitier;
  
  // Calculer le statut des mesures (cohérent avec BainDetail)
  // 'all_ok' = tous les champs ont des valeurs, 'partial' = certains null, 'none' = pas de données
  const getSensorsStatus = (): 'disabled' | 'all_ok' | 'partial' | 'none' => {
    if (!mqtt_connected) return 'disabled';
    if (!last_reading || !fields || fields.length === 0) return 'none';
    
    let hasValue = 0;
    let hasNull = 0;
    for (const field of fields) {
      const value = last_reading[field.field_code];
      if (value !== null && value !== undefined) {
        hasValue++;
      } else {
        hasNull++;
      }
    }
    
    if (hasValue === fields.length) return 'all_ok';
    if (hasValue > 0) return 'partial';
    return 'none';
  };
  
  const sensorsStatus = getSensorsStatus();
  const sensorsChipColor = sensorsStatus === 'all_ok' ? 'success' : sensorsStatus === 'partial' ? 'warning' : sensorsStatus === 'none' ? 'error' : 'default';
  const sensorsChipIcon = sensorsStatus === 'all_ok' || sensorsStatus === 'partial' ? <SensorsIcon /> : <SensorsOffIcon />;
  
  // Regle de nommage partagee par toutes les vues — cf. utils/nomBoitier.
  const getDisplayName = () => nomAffichageBoitier({ boitier_id, name, hostname: (boitier as any).hostname });

  // Format de la dernière mesure vue
  const formatLastSeen = (dateString: string | null) => {
    if (!dateString) return 'Jamais';
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy HH:mm:ss', { locale: fr });
    } catch (e) {
      return 'Date invalide';
    }
  };

  return (
    <Card 
      sx={{
        minWidth: 275,
        height: HAUTEUR_CARTE_BAIN,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: `6px solid ${alerts.length > 0 
          ? '#f44336' 
          : sensorsStatus === 'all_ok'
          ? '#4caf50' 
          : sensorsStatus === 'partial'
          ? '#ff9800'
          : sensorsStatus === 'none'
          ? '#f44336'
          : '#9e9e9e'}`
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        <CardContent
          sx={{
            flexGrow: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: `${ESPACEMENT}px`,
            p: `${PADDING_CARTE}px`,
            overflow: 'hidden',
            // MUI ajoute 24px de padding bas sur le dernier enfant : le
            // gabarit ne tomberait plus juste.
            '&:last-child': { pb: `${PADDING_CARTE}px` },
          }}
        >
          {/* ---- Zone 1 : en-tete = titre + pastilles collees dessous ---- */}
          <Box
            sx={{
              height: ZONE.entete,
              flexShrink: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '4px',
            }}
          >
            <Typography
              component="div"
              title={getDisplayName()}
              sx={{
                fontSize: '1.05rem',
                fontWeight: 600,
                lineHeight: 1.25,
                ...LIGNES_MAX(2),
              }}
            >
              {getDisplayName()}
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ flexWrap: 'nowrap', '& > *': { flexShrink: 0 } }}
            >
              <PastillePresence
                presence={presence || (boitier as any).presence}
                supervision={supervision}
              />
              <Chip
                icon={mqtt_connected ? <WifiIcon /> : <WifiOffIcon />}
                label="MQTT"
                color={mqtt_connected ? "success" : "default"}
                size="small"
                sx={{ fontSize: '0.75rem' }}
              />
              <Chip
                icon={sensorsChipIcon}
                label="Mesures"
                color={sensorsChipColor}
                size="small"
                sx={{ fontSize: '0.75rem' }}
              />
              {regulation_enabled && (
                <Chip
                  icon={<SettingsInputComponentIcon />}
                  label="Régulé"
                  color="info"
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
            </Stack>
          </Box>
          
          {/* ---- Zone 2 : mesures, grille fixe 3 x 3 ---- */}
          <Box
            sx={{
              height: ZONE.mesures,
              flexShrink: 0,
              overflow: 'hidden',
              // Griser si hors ligne
              opacity: (mqtt_connected || sensors_active) ? 1 : 0.5,
              filter: (mqtt_connected || sensors_active) ? 'none' : 'grayscale(50%)',
            }}
          >
            {last_reading ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${COLONNES_MESURES}, minmax(0, 1fr))`,
                  gridAutoRows: `${HAUTEUR_CELLULE}px`,
                  gap: `${ESPACEMENT}px`,
                  alignContent: 'start',
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                {(() => {
                  // [...fields] : ne jamais trier en place le tableau porte
                  // par les props, cela reordonne l'etat du parent.
                  const tous = fields && fields.length > 0
                    ? [...fields].sort((a, b) => a.display_order - b.display_order)
                    : Object.entries(last_reading)
                        .filter(([key]) => key !== 'ts' && !key.startsWith('relay'))
                        .map(([key]) => ({ field_code: key, field_name: key, unit: '', precision_digits: 2 }));

                  const tronque = tous.length > MAX_MESURES;
                  const affiches = tous.slice(0, tronque ? MAX_MESURES - 1 : MAX_MESURES);

                  const cellules = affiches.map((field: any) => {
                    const IconComponent = field.icon ? getFieldIcon(field) : getDefaultIcon(field.field_code);
                    const value = last_reading[field.field_code];
                    return (
                      <Box key={field.field_code} sx={{ minWidth: 0 }}>
                        <Typography
                          color="text.secondary"
                          title={field.field_name}
                          sx={{ fontSize: '0.78rem', lineHeight: 1.4, ...UNE_LIGNE }}
                        >
                          <IconComponent fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          {field.field_name}
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 500, lineHeight: 1.3, ...UNE_LIGNE }}>
                          {formatValue(value, field.precision_digits || 2)} {field.unit || ''}
                        </Typography>
                      </Box>
                    );
                  });

                  // Au-dela de MAX_MESURES, la derniere case affiche le nombre de
                  // mesures non montrees.
                  if (tronque) {
                    cellules.push(
                      <Box key="__reste" sx={{ minWidth: 0, alignSelf: 'center' }}>
                        <Typography color="text.secondary" sx={{ fontSize: '0.85rem', ...UNE_LIGNE }}>
                          +{tous.length - (MAX_MESURES - 1)} autres
                        </Typography>
                      </Box>
                    );
                  }
                  return cellules;
                })()}
              </Box>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">Aucune donnée disponible</Typography>
              </Box>
            )}
          </Box>

          {/* ---- Zone 3 : horodatage de la derniere mesure ---- */}
          <Typography
            variant="caption"
            display="block"
            sx={{
              height: ZONE.horodatage,
              flexShrink: 0,
              lineHeight: `${ZONE.horodatage}px`,
              textAlign: 'right',
              color: (mqtt_connected || sensors_active) ? 'text.secondary' : 'error.main',
              ...UNE_LIGNE,
            }}
          >
            {last_reading
              ? `${(mqtt_connected || sensors_active) ? '' : '⚠️ Hors ligne - '}Dernière mesure : ${format(new Date(last_reading.ts), 'HH:mm:ss', { locale: fr })}`
              : 'Aucune mesure reçue'}
          </Typography>
          
          {/* ---- Zone 4 : relais (reservee meme sans regulation) ---- */}
          <Box sx={{ height: ZONE.relais, flexShrink: 0, overflow: 'hidden' }}>
            {regulation_enabled && relays && relays.length > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ flexWrap: 'nowrap', '& > *': { flexShrink: 0 } }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                  Relais :
                </Typography>
                {relays.filter(r => r.is_enabled).map((relay) => {
                  // Griser si boîtier hors ligne
                  const isOnline = mqtt_connected || sensors_active;
                  // Priorité: état temps réel depuis last_reading (0/1), sinon depuis config
                  const relayKey = `relay${relay.relay_number}`;
                  const rawValue = last_reading ? last_reading[relayKey] : null;
                  // Convertir 0/1 en booléen (InfluxDB retourne des floats/int)
                  const realTimeState = rawValue !== undefined && rawValue !== null
                    ? Number(rawValue) === 1
                    : relay.current_state;
                  return (
                    <Chip
                      key={relay.relay_number}
                      icon={realTimeState ? <PowerIcon /> : <PowerOffIcon />}
                      label={`${relay.name || `R${relay.relay_number}`}: ${isOnline ? (realTimeState ? 'ON' : 'OFF') : '?'}`}
                      color={isOnline ? (realTimeState ? "success" : "default") : "default"}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.7rem',
                        opacity: isOnline ? 1 : 0.5,
                        filter: isOnline ? 'none' : 'grayscale(100%)'
                      }}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* ---- Zone 5 : alertes — trait toujours visible, « RAS » si rien ---- */}
          <Box
            sx={{
              height: ZONE.alertes,
              flexShrink: 0,
              overflow: 'hidden',
              borderTop: 1,
              borderColor: 'divider',
              pt: 0.5,
            }}
          >
            <Stack direction="row" spacing={1}>
              {alerts.length > 0 ? (
                <WarningIcon color="error" fontSize="small" sx={{ flexShrink: 0, mt: '2px' }} />
              ) : (
                <CheckCircleOutlineIcon color="success" fontSize="small" sx={{ flexShrink: 0, mt: '2px' }} />
              )}
              <Typography
                color={alerts.length > 0 ? 'error.main' : 'text.secondary'}
                variant="body2"
                title={alerts.length > 0 ? alerts.join(' — ') : 'Rien à signaler'}
                sx={{ fontSize: '0.8rem', lineHeight: 1.25, ...LIGNES_MAX(2) }}
              >
                {alerts.length === 0
                  ? 'RAS'
                  : alerts.length === 1
                  ? alerts[0]
                  : `${alerts.length} alertes actives`}
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default BainCard;
